import { Link } from 'react-router-dom';
import { useCMS } from '@/hooks/useCMS';

const DEFAULT = {
  logo: 'https://public.readdy.ai/ai/img_res/45e9b15f-dd9c-4fb5-969b-d78da4754238.png',
  logo_alt: 'Phoenix',
  tagline: 'საქართველოს წამყვანი პრემიუმ სამშენებლო მასალების მომწოდებელი 1999 წლიდან.',
  social_links: [
    { icon: 'ri-facebook-fill', href: '#', label: 'Facebook' },
    { icon: 'ri-instagram-line', href: '#', label: 'Instagram' },
    { icon: 'ri-linkedin-fill',  href: '#', label: 'LinkedIn' },
  ],
  products_nav_heading: 'პროდუქტები',
  products_nav: [
    { label: 'კატეგორიები', path: '/categories' },
    { label: 'მაღაზია',     path: '/shop' },
    { label: 'ბრენდები',    path: '/brands' },
    { label: 'სერვისები',   path: '/services' },
    { label: 'ბლოგი',       path: '/blog' },
  ],
  company_nav_heading: 'კომპანია',
  company_nav: [
    { label: 'მთავარი',      path: '/' },
    { label: 'ჩვენ შესახებ', path: '/about' },
    { label: 'სერვისები',    path: '/services' },
    { label: 'კონტაქტი',     path: '/contact' },
  ],
  contact_heading: 'კონტაქტი',
  phone_primary: '+995 599 87 20 20',
  phone_primary_href: 'tel:+995599872020',
  email: 'info@phoenix.ge',
  email_href: 'mailto:info@phoenix.ge',
  address_line1: 'გელოვანის ქ. N13,',
  address_line2: 'თბილისი, საქართველო',
  hours: 'ორშ–შაბ: 09:30–19:30',
  legal_links: [
    { label: 'კონფიდენციალურობა',   path: '/privacy' },
    { label: 'გამოყენების პირობები', path: '/terms' },
    { label: 'Cookie პოლიტიკა',      path: '/cookies' },
    { label: 'დაბრუნება',            path: '/refund' },
    { label: 'მიწოდება',             path: '/shipping' },
  ],
  copyright_suffix: 'Phoenix Georgia. ყველა უფლება დაცულია.',
  location_badge: 'Tbilisi, Georgia',
};

export default function Footer() {
  const cms = useCMS('footer', DEFAULT);

  const socials: typeof DEFAULT.social_links = cms.social_links ?? DEFAULT.social_links;
  const productsNav: typeof DEFAULT.products_nav = cms.products_nav ?? DEFAULT.products_nav;
  const companyNav: typeof DEFAULT.company_nav = cms.company_nav ?? DEFAULT.company_nav;
  const legalLinks: typeof DEFAULT.legal_links = cms.legal_links ?? DEFAULT.legal_links;

  return (
    <footer className="bg-[#F5F2EE] border-t border-[#1C1C1C]/8">
      <div className="max-w-7xl mx-auto px-8 md:px-14 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8 mb-16">
          {/* Brand col */}
          <div className="md:col-span-4">
            <Link to="/" className="inline-block mb-6 cursor-pointer">
              <img src={cms.logo} alt={cms.logo_alt} className="h-10 w-auto object-contain brightness-0" />
            </Link>
            <p className="text-[#1C1C1C]/45 text-sm leading-relaxed mb-6 max-w-xs">{cms.tagline}</p>
            <div className="flex items-center gap-3">
              {socials.map((s, i) => (
                <a key={i} href={s.href} aria-label={s.label}
                  className="w-8 h-8 border border-[#1C1C1C]/15 flex items-center justify-center text-[#1C1C1C]/40 hover:border-[#ec3145] hover:text-[#ec3145] transition-all duration-300 cursor-pointer">
                  <i className={`${s.icon} text-xs`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Products */}
          <div className="md:col-span-3">
            <h4 className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/30 mb-5">{cms.products_nav_heading}</h4>
            <ul className="flex flex-col gap-2.5">
              {productsNav.map((link) => (
                <li key={link.label}>
                  <Link to={link.path} className="text-sm text-[#1C1C1C]/55 hover:text-[#ec3145] transition-colors duration-300 cursor-pointer">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="md:col-span-2">
            <h4 className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/30 mb-5">{cms.company_nav_heading}</h4>
            <ul className="flex flex-col gap-2.5">
              {companyNav.map((link) => (
                <li key={link.label}>
                  <Link to={link.path} className="text-sm text-[#1C1C1C]/55 hover:text-[#ec3145] transition-colors duration-300 cursor-pointer">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h4 className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/30 mb-5">{cms.contact_heading}</h4>
            <ul className="flex flex-col gap-3">
              <li>
                <a href={cms.phone_primary_href} className="text-sm text-[#1C1C1C]/55 hover:text-[#ec3145] transition-colors duration-300 cursor-pointer flex items-center gap-2">
                  <i className="ri-phone-line text-xs text-[#ec3145]"></i>{cms.phone_primary}
                </a>
              </li>
              <li>
                <a href={cms.email_href} className="text-sm text-[#1C1C1C]/55 hover:text-[#ec3145] transition-colors duration-300 cursor-pointer flex items-center gap-2">
                  <i className="ri-mail-line text-xs text-[#ec3145]"></i>{cms.email}
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-[#1C1C1C]/45">
                <i className="ri-map-pin-line text-xs text-[#ec3145] mt-0.5 flex-shrink-0"></i>
                <span>{cms.address_line1}<br />{cms.address_line2}</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-[#1C1C1C]/45">
                <i className="ri-time-line text-xs text-[#ec3145]"></i>{cms.hours}
              </li>
            </ul>
          </div>
        </div>

        {/* Legal links row */}
        <div className="border-t border-[#1C1C1C]/8 pt-6 pb-4">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {legalLinks.map((link, i) => (
              <span key={link.label} className="flex items-center gap-x-6">
                {i > 0 && <span className="text-[#1C1C1C]/10">|</span>}
                <Link to={link.path} className="text-xs text-[#1C1C1C]/30 hover:text-[#ec3145] transition-colors cursor-pointer whitespace-nowrap">
                  {link.label}
                </Link>
              </span>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-[#1C1C1C]/8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#1C1C1C]/25">
            &copy; {new Date().getFullYear()} {cms.copyright_suffix}
          </p>
          <div className="flex items-center gap-2">
            <div className="w-1 h-1 rounded-full bg-[#ec3145]"></div>
            <span className="text-xs text-[#1C1C1C]/25">{cms.location_badge}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
